import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Bell, CheckCheck, Inbox, MessageSquareReply, RefreshCw, Siren, ServerOff } from "lucide-react";
import { fmtRel } from "./supportUtil";

const KIND = {
  new: { label: "Tiket baru", icon: Inbox, tint: "bg-sky-50 text-sky-700" },
  reply: { label: "Balasan pengguna", icon: MessageSquareReply, tint: "bg-emerald-50 text-emerald-700" },
  escalated: { label: "Eskalasi L2", icon: ArrowUpRight, tint: "bg-violet-50 text-violet-700" },
  health_crit: { icon: Siren, tint: "bg-rose-50 text-rose-700" },
  health_offline: { icon: ServerOff, tint: "bg-rose-50 text-rose-700" },
};

function NotifItem({ n, onOpen, testid }) {
  const k = KIND[n.kind], Icon = k.icon, hot = n.kind.startsWith("health");
  return (
    <li>
      <button onClick={() => onOpen(n)} className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "opacity-60" : ""}`} data-testid={testid}>
        <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${k.tint}`}><Icon size={15} /></span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-[11px]"><span className={`font-bold ${hot ? "text-rose-700" : "text-slate-700"}`}>{n.label}</span>{n.meta && <span className="font-mono text-slate-400">{n.meta}</span>}</span>
          <span className={`mt-0.5 block truncate text-sm font-semibold text-slate-900 ${hot ? "font-mono" : ""}`}>{n.title}</span>
          <span className="block text-[11px] text-slate-500">{n.sub} · {hot ? "sejak " : ""}{fmtRel(n.at)}</span>
        </span>
        {!n.read && <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${hot ? "bg-rose-500" : "bg-brand"}`} data-testid={`${testid}-dot`} />}
      </button>
    </li>
  );
}

export function NotificationBell({ notifs, idSuffix = "" }) {
  const [open, setOpen] = useState(false); const ref = useRef(null); const nav = useNavigate();
  const tid = (s) => `notif-${s}${idSuffix}`;
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", h); window.addEventListener("keydown", k);
    return () => { document.removeEventListener("mousedown", h); window.removeEventListener("keydown", k); };
  }, [open]);
  const openItem = (n) => { notifs.markRead(n.key); setOpen(false); nav(n.to); };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => { setOpen((o) => !o); if (!open) notifs.refresh(); }} className="relative rounded-xl border border-line bg-white p-2 text-slate-700 transition-transform hover:bg-slate-50 active:scale-95" aria-label="Notifikasi" data-testid={tid("bell")}>
        <Bell size={18} />
        {notifs.healthCount > 0 && <span className="pointer-events-none absolute inset-0 animate-pulse rounded-xl ring-2 ring-rose-400" data-testid={tid("health-pulse")} />}
        {notifs.unread > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white ring-2 ring-white" data-testid={tid("count")}>{notifs.unread > 99 ? "99+" : notifs.unread}</span>}
      </button>
      {open && (
        <div className="fade-up absolute right-0 top-full z-[60] mt-2 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-line bg-white shadow-2xl" data-testid={tid("panel")}>
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <p className="flex-1 text-sm font-bold text-slate-900">Notifikasi</p>
            <button onClick={notifs.refresh} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Segarkan" data-testid={tid("refresh")}><RefreshCw size={13} className={notifs.loading ? "animate-spin" : ""} /></button>
            <button onClick={notifs.markAll} disabled={!notifs.unread} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold text-brand hover:bg-brand-soft disabled:opacity-40" data-testid={tid("mark-all")}><CheckCheck size={13} /> Tandai semua dibaca</button>
          </div>
          {notifs.items.length === 0
            ? <p className="px-4 py-10 text-center text-sm text-slate-400" data-testid={tid("empty")}>Tidak ada notifikasi.</p>
            : <ul className="max-h-[60vh] divide-y divide-line overflow-y-auto" data-testid={tid("list")}>{notifs.items.map((n) => <NotifItem key={n.key} n={n} onOpen={openItem} testid={`${tid("item")}-${n.tid}`} />)}</ul>}
        </div>
      )}
    </div>
  );
}
